import { ApiResponse } from '../types';

// 用戶相關接口
export interface User {
  _id: string;
  username: string;
  email: string;
  role: 'user' | 'operator' | 'admin';
  isActive: boolean;
  profile?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  role?: string;
  status?: string;
  search?: string;
}

export interface UsersResponse {
  users: User[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

// 系統配置接口
export interface SystemConfig {
  parkingLot: {
    name: string;
    totalSpots: number;
    floors: number;
    zones: string[];
  };
  pricing: {
    hourlyRate: number;
    dailyRate: number;
    currency: string;
  };
  features: {
    aiRecognition: boolean;
    jetsonThor: boolean;
    gemmaModel: boolean;
  };
  notifications: {
    email: boolean;
    sms: boolean;
  };
}

// 系統監控接口
export interface SystemHealth {
  status: 'healthy' | 'warning' | 'error';
  checks: {
    database: boolean;
    activeConnections: number;
    memoryUsage: {
      rss: number;
      heapTotal: number;
      heapUsed: number;
      external: number;
    };
    uptime: number;
    timestamp: string;
  };
  issues: string[];
  metrics: {
    totalSpots: number;
    occupiedSpots: number;
    occupancyRate: string;
    overdueSessions: number;
    maintenanceSpots: number;
  };
}

export interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalSpots: number;
  occupiedSpots: number;
  systemHealth: 'healthy' | 'warning' | 'error';
  uptime: number;
}

// 系統日誌接口
export interface SystemLog {
  _id: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  timestamp: string;
  source: string;
  userId?: string;
  metadata?: any;
}

export interface LogFilters {
  page?: number;
  limit?: number;
  level?: string;
  source?: string;
  timeRange?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface LogsResponse {
  logs: SystemLog[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

// 系統操作接口
export interface CleanupResult {
  sessionsDeleted: number;
  paymentsDeleted: number;
  cutoffDate: string;
}

export interface BackupInfo {
  timestamp: string;
  type: 'manual' | 'auto';
  status: 'completed' | 'failed' | 'in_progress';
  size: string;
  location: string;
}

class AdminService {
  private baseURL = `${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/admin`;

  // 用戶管理
  async getUsers(filters: UserFilters = {}): Promise<ApiResponse<UsersResponse>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await fetch(`${this.baseURL}/users?${params}`);
    return response.json();
  }

  async getUser(userId: string): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${this.baseURL}/users/${userId}`);
    return response.json();
  }

  async createUser(userData: Partial<User> & { password: string }): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${this.baseURL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    return response.json();
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${this.baseURL}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    return response.json();
  }

  async updateUserRole(userId: string, role: string): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${this.baseURL}/users/${userId}/role`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role }),
    });
    return response.json();
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${this.baseURL}/users/${userId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ isActive }),
    });
    return response.json();
  }

  async deleteUser(userId: string): Promise<ApiResponse<{}>> {
    const response = await fetch(`${this.baseURL}/users/${userId}`, {
      method: 'DELETE',
    });
    return response.json();
  }

  // 系統配置
  async getSystemConfig(): Promise<ApiResponse<{ config: SystemConfig }>> {
    const response = await fetch(`${this.baseURL}/config`);
    return response.json();
  }

  async updateSystemConfig(config: SystemConfig): Promise<ApiResponse<{ config: SystemConfig }>> {
    const response = await fetch(`${this.baseURL}/config`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ config }),
    });
    return response.json();
  }

  // 系統監控
  async getSystemHealth(): Promise<ApiResponse<SystemHealth>> {
    const response = await fetch(`${this.baseURL}/status/health`);
    return response.json();
  }

  async getSystemStats(): Promise<ApiResponse<SystemStats>> {
    // 這個API需要從多個端點聚合數據
    const [healthResponse, usersResponse] = await Promise.all([
      fetch(`${this.baseURL}/status/health`),
      fetch(`${this.baseURL}/users?limit=1`)
    ]);

    const healthData = await healthResponse.json();
    const usersData = await usersResponse.json();

    const stats: SystemStats = {
      totalUsers: usersData.data?.pagination?.total || 0,
      activeUsers: 0, // 需要從用戶數據中計算
      totalSpots: healthData.data?.metrics?.totalSpots || 0,
      occupiedSpots: healthData.data?.metrics?.occupiedSpots || 0,
      systemHealth: healthData.data?.status || 'healthy',
      uptime: healthData.data?.checks?.uptime || 0,
    };

    return {
      success: true,
      data: stats,
      message: '系統統計獲取成功'
    };
  }

  // 系統日誌
  async getSystemLogs(filters: LogFilters = {}): Promise<ApiResponse<LogsResponse>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await fetch(`${this.baseURL}/logs?${params}`);
    return response.json();
  }

  // 系統操作
  async cleanupOldData(days: number = 90): Promise<ApiResponse<CleanupResult>> {
    const response = await fetch(`${this.baseURL}/cleanup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ days }),
    });
    return response.json();
  }

  async createBackup(): Promise<ApiResponse<{ backup: BackupInfo }>> {
    const response = await fetch(`${this.baseURL}/backup`, {
      method: 'POST',
    });
    return response.json();
  }
}

export const adminService = new AdminService();